import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, InputBase, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { ExpandMore, Refresh, Search } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { DateTime } from 'luxon';
import { TooltipMenu } from '../../../../../common/tooltip-menu';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { PendingRequestFilters, PendingRequestsFilterOptions } from './pending-requests-types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    option: {
      color: theme.alert.blue.extraLight,
      fontSize: '14px',
      textTransform: 'none',
      '& span': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
    },
    optionList: {
      overflowY: 'auto',
      maxHeight: 'calc(30vh - 40px)',
      width: '100%',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
      },
    },
    dateInput: {
      background: 'rgba(255,255,255,0.1)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '4px',
      padding: '4px 6px',
      fontSize: '12px',
      colorScheme: 'dark',
    },
  })
);

interface CheckboxMenuProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  label: string;
  options: Array<{ id: string; name: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}

const CheckboxFilterMenu = ({ open, onOpen, onClose, label, options, selected, onToggle }: CheckboxMenuProps) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [search, setSearch] = useState('');

  const visibleOptions = options.filter((option) => option.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <TooltipMenu
      open={open}
      onClose={() => {
        onClose();
        setSearch('');
      }}
      title={
        <Box
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
          style={{ padding: '5px 10px', maxHeight: '30vh', minWidth: '200px' }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', padding: '6px 0 4px', width: '100%' }}>
            <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
            <InputBase
              placeholder='Search'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ color: '#fff', fontSize: '12px', flex: 1 }}
            />
          </Box>
          <Box className={classes.optionList}>
            {visibleOptions.length === 0 && (
              <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', padding: '6px 0' }}>
                No options
              </Typography>
            )}
            {visibleOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                className={classes.option}
                control={
                  <Checkbox
                    style={{ color: '#fff' }}
                    checked={selected.includes(option.id)}
                    onChange={() => onToggle(option.id)}
                  />
                }
                label={option.name}
                style={{ display: 'flex' }}
              />
            ))}
          </Box>
        </Box>
      }
    >
      <TabletButton
        size='small'
        aria-haspopup='true'
        onClick={onOpen}
        endIcon={<ExpandMore />}
        style={{ border: 'none', padding: '2px 7px', marginRight: '10px', color: theme.color.grey.neutral300 }}
      >
        <strong>{selected.length === 0 ? 'All' : selected.length}</strong>&nbsp;{label}
      </TabletButton>
    </TooltipMenu>
  );
};

const formatShortDate = (date: string) => DateTime.fromISO(date).toFormat('dd/MM/yy');

type FilterMenu = 'programs' | 'activities' | 'date';

interface Props {
  filters: PendingRequestFilters;
  filterOptions: PendingRequestsFilterOptions;
  onChange: (changes: Partial<PendingRequestFilters>) => void;
  onReset: () => void;
  dateLabel: string;
}

export const PendingRequestsFilterBar = ({ filters, filterOptions, onChange, onReset, dateLabel }: Props) => {
  const classes = useStyles();
  const { theme, isMobile, isSm } = useCampfireTheme();
  const [searchInput, setSearchInput] = useState(filters.query);
  const [openMenu, setOpenMenu] = useState<FilterMenu | null>(null);

  const closeMenu = (menu: FilterMenu) => setOpenMenu((current) => (current === menu ? null : current));

  useEffect(() => {
    setSearchInput(filters.query);
  }, [filters.query]);

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const activityOptions = filterOptions.activities.filter(
    (activity) => filters.programIds.length === 0 || filters.programIds.includes(activity.programId)
  );

  const onToggleProgram = (programId: string) => {
    const programIds = toggle(filters.programIds, programId);
    const allowedActivityIds = filterOptions.activities
      .filter((activity) => programIds.length === 0 || programIds.includes(activity.programId))
      .map((activity) => activity.activityId);
    onChange({
      programIds,
      activityIds: filters.activityIds.filter((activityId) => allowedActivityIds.includes(activityId)),
    });
  };

  const dateButtonLabel = (() => {
    if (filters.dateFrom && filters.dateTo) {
      return `${formatShortDate(filters.dateFrom)} - ${formatShortDate(filters.dateTo)}`;
    }
    if (filters.dateFrom) return `From ${formatShortDate(filters.dateFrom)}`;
    if (filters.dateTo) return `Until ${formatShortDate(filters.dateTo)}`;
    return 'Any';
  })();

  const isCompact = isMobile || isSm;

  return (
    <Box
      style={{
        width: isCompact ? '95%' : '50%',
        marginBottom: isCompact ? '20px' : '32px',
        marginTop: '20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        flexShrink: 0,
      }}
    >
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <InputBase
          value={searchInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onChange({ query: searchInput });
          }}
          placeholder={isCompact ? 'Search' : 'Search by name, email, phone number'}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{
            border: '.5px solid #d9d9d9',
            borderRadius: '25px 0 0 25px',
            width: '85%',
            padding: '6px 24px',
          }}
        />
        <Button
          style={{ backgroundColor: '#7047EB', borderRadius: '0 25px 25px 0', width: '12%', padding: '8px 10px' }}
          onClick={() => onChange({ query: searchInput })}
          aria-label='Search'
        >
          <Search style={{ color: '#fff' }} />
        </Button>
      </Box>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '12px',
          flexWrap: isCompact ? 'wrap' : 'nowrap',
          gap: isCompact ? '8px' : undefined,
        }}
      >
        <CheckboxFilterMenu
          open={openMenu === 'programs'}
          onOpen={() => setOpenMenu('programs')}
          onClose={() => closeMenu('programs')}
          label='Programs'
          options={filterOptions.programs.map((program) => ({ id: program.programId, name: program.name }))}
          selected={filters.programIds}
          onToggle={onToggleProgram}
        />
        <CheckboxFilterMenu
          open={openMenu === 'activities'}
          onOpen={() => setOpenMenu('activities')}
          onClose={() => closeMenu('activities')}
          label='Activities'
          options={activityOptions.map((activity) => ({ id: activity.activityId, name: activity.name }))}
          selected={filters.activityIds}
          onToggle={(activityId) => onChange({ activityIds: toggle(filters.activityIds, activityId) })}
        />
        <TooltipMenu
          open={openMenu === 'date'}
          onClose={() => closeMenu('date')}
          title={
            <Box style={{ padding: '8px 10px' }}>
              <Typography style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '6px' }}>
                {dateLabel} between
              </Typography>
              <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type='date'
                  className={classes.dateInput}
                  value={filters.dateFrom ?? ''}
                  max={filters.dateTo ?? undefined}
                  onChange={(e) => onChange({ dateFrom: e.target.value || null })}
                />
                <input
                  type='date'
                  className={classes.dateInput}
                  value={filters.dateTo ?? ''}
                  min={filters.dateFrom ?? undefined}
                  onChange={(e) => onChange({ dateTo: e.target.value || null })}
                />
              </Box>
            </Box>
          }
        >
          <TabletButton
            size='small'
            aria-haspopup='true'
            onClick={() => setOpenMenu('date')}
            endIcon={<ExpandMore />}
            style={{ border: 'none', padding: '2px 7px', marginRight: '10px', color: theme.color.grey.neutral300 }}
          >
            {dateLabel}:&nbsp;<strong>{dateButtonLabel}</strong>
          </TabletButton>
        </TooltipMenu>
        <TabletButton
          size='small'
          onClick={() => {
            setSearchInput('');
            onReset();
          }}
          startIcon={<Refresh />}
          style={{ border: 'none', padding: '2px 7px', color: theme.color.grey.neutral300 }}
        >
          <strong>Reset</strong>
        </TabletButton>
      </Box>
    </Box>
  );
};
